'use client';

import { useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  AlertTriangle,
  Check,
  ExternalLink,
  HelpCircle,
  ImageUp,
  Loader2,
  ScanLine,
  Trash2,
  ZoomIn
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { UploadProgress } from '@/components/upload-progress';
import { renderPdfToImages } from '../utils/pdf-to-images';
import {
  useQuarantineStore,
  type QuarantineItem
} from '../store/quarantine-store';

interface QrQuarantinePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  total: number;
  /**
   * Page numbers (1-based slot sequences) already taken by a persisted
   * `success` page on the server — a manually-assigned slot can't collide
   * with one of these.
   */
  occupiedPageNumbers: number[];
  /** Whether the scanner is wired up (enables the per-item re-scan button). */
  scannerAvailable: boolean;
  /** Trigger a scanner acquisition that replaces this held item's bytes. */
  onRescanItem: (localId: string) => void;
  /** Replace a held item's bytes from an uploaded image/PDF page. */
  onReuploadItem: (localId: string, blob: Blob) => void | Promise<void>;
  /** Submit every resolved/accepted item to the server in one go. */
  onSubmit: () => void | Promise<void>;
  isSubmitting: boolean;
  /**
   * Byte-level progress for an in-flight re-upload / submit (owned by the
   * parent, which runs the upload mutation). `null`/`undefined` while idle.
   */
  uploadProgress?: number | null;
}

export function QrQuarantinePanel({
  open,
  onOpenChange,
  total,
  occupiedPageNumbers,
  scannerAvailable,
  onRescanItem,
  onReuploadItem,
  onSubmit,
  isSubmitting,
  uploadProgress
}: QrQuarantinePanelProps) {
  const t = useTranslations('photoScanning');
  const items = useQuarantineStore((s) => s.items);

  const resolvedCount = useMemo(
    () => items.filter((i) => i.resolution !== 'pending').length,
    [items]
  );
  const pendingCount = items.length - resolvedCount;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[85vh] gap-4 sm:max-w-3xl'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <AlertTriangle className='h-4 w-4 text-amber-500' aria-hidden />
            {t('needsActionTitle')}
          </DialogTitle>
          <DialogDescription>{t('needsActionDescription')}</DialogDescription>
        </DialogHeader>

        {items.length === 0 ? (
          <div className='text-muted-foreground flex h-40 items-center justify-center text-sm'>
            {t('attentionBannerEmpty')}
          </div>
        ) : (
          <ScrollArea className='max-h-[55vh] rounded-md border'>
            <div className='flex flex-col gap-2 p-3'>
              {items.map((item) => (
                <QuarantineRow
                  key={item.localId}
                  item={item}
                  total={total}
                  occupiedPageNumbers={occupiedPageNumbers}
                  scannerAvailable={scannerAvailable}
                  onRescanItem={onRescanItem}
                  onReuploadItem={onReuploadItem}
                />
              ))}
            </div>
          </ScrollArea>
        )}

        {/* Byte-level upload progress for re-upload / submit. Renders nothing
            while idle. */}
        <UploadProgress value={uploadProgress} />

        <DialogFooter className='items-center sm:justify-between'>
          <span className='text-muted-foreground text-xs'>
            {pendingCount > 0
              ? t('heldForReview', { count: pendingCount })
              : null}
          </span>
          <Button
            onClick={() => void onSubmit()}
            disabled={resolvedCount === 0 || isSubmitting}
          >
            {isSubmitting && (
              <Loader2 className='me-2 h-4 w-4 animate-spin' aria-hidden />
            )}
            {t('submitResolved', { count: resolvedCount })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Per-item row ────────────────────────────────────────────────────────────

function QuarantineRow({
  item,
  total,
  occupiedPageNumbers,
  scannerAvailable,
  onRescanItem,
  onReuploadItem
}: {
  item: QuarantineItem;
  total: number;
  occupiedPageNumbers: number[];
  scannerAvailable: boolean;
  onRescanItem: (localId: string) => void;
  onReuploadItem: (localId: string, blob: Blob) => void | Promise<void>;
}) {
  const t = useTranslations('photoScanning');
  const removeItem = useQuarantineStore((s) => s.removeItem);
  const acceptItem = useQuarantineStore((s) => s.acceptItem);
  const reuploadRef = useRef<HTMLInputElement>(null);
  const [isRendering, setIsRendering] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const isMismatch = item.classification === 'mismatched';
  const reason = isMismatch ? t('qrMismatch') : t('noQrDetected');

  // Page numbers taken by other ACCEPTED held items — a manual slot can't
  // collide with another item the operator already pinned to a number.
  const items = useQuarantineStore((s) => s.items);

  const otherAcceptedNumbers = useMemo(() => {
    const numbers: number[] = [];

    for (const i of items) {
      if (
        i.localId !== item.localId &&
        i.resolution === 'accepted' &&
        i.assignedPageNumber != null
      ) {
        numbers.push(i.assignedPageNumber);
      }
    }

    return numbers;
  }, [items, item.localId]);
  const taken = useMemo(
    () => new Set([...occupiedPageNumbers, ...otherAcceptedNumbers]),
    [occupiedPageNumbers, otherAcceptedNumbers]
  );

  async function handleReupload(file: File) {
    // A PDF resolves to its first rendered page; an image is used directly.
    if (file.type === 'application/pdf') {
      setIsRendering(true);
      try {
        const rendered = await renderPdfToImages(file, {
          scale: 2,
          mimeType: 'image/jpeg',
          quality: 0.92,
          maxPages: 1
        });
        const first = rendered[0];
        if (first) await onReuploadItem(item.localId, first.blob);
      } finally {
        setIsRendering(false);
      }
      return;
    }
    await onReuploadItem(item.localId, file);
  }

  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-lg border bg-white p-2.5 dark:bg-slate-900',
        isMismatch
          ? 'border-rose-300 dark:border-rose-700/50'
          : 'border-amber-300 dark:border-amber-700/50'
      )}
    >
      {/* Thumbnail — click to enlarge so the operator can read the page
          clearly before choosing an action. */}
      <button
        type='button'
        onClick={() => setPreviewOpen(true)}
        aria-label={t('previewImage')}
        title={t('previewImage')}
        className='group relative h-20 w-16 shrink-0 cursor-zoom-in overflow-hidden rounded-md border bg-slate-100 dark:bg-slate-800'
      >
        {/* eslint-disable-next-line @next/next/no-img-element -- in-memory object URL preview; next/image cannot proxy blob:// schemes */}
        <img
          src={item.objectUrl}
          alt=''
          className='h-full w-full object-cover'
          draggable={false}
        />
        <span className='absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all group-hover:bg-black/45 group-hover:opacity-100'>
          <ZoomIn className='h-4 w-4 text-white' aria-hidden />
        </span>
      </button>

      {/* Full-size preview lightbox */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className='sm:max-w-4xl'>
          <DialogHeader>
            <div className='flex items-center justify-between gap-2 pe-8'>
              <DialogTitle className='flex items-center gap-2 text-sm'>
                {isMismatch ? (
                  <AlertTriangle
                    className='h-4 w-4 shrink-0 text-rose-500'
                    aria-hidden
                  />
                ) : (
                  <HelpCircle
                    className='h-4 w-4 shrink-0 text-amber-500'
                    aria-hidden
                  />
                )}
                {isMismatch ? t('mismatchLabel') : t('unverifiedLabel')}
                {item.qrData ? (
                  <span className='font-mono text-xs opacity-70'>
                    ({item.qrData})
                  </span>
                ) : null}
              </DialogTitle>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='h-7 w-7 shrink-0'
                onClick={() =>
                  window.open(item.objectUrl, '_blank', 'noopener,noreferrer')
                }
                aria-label={t('openInNewTab')}
                title={t('openInNewTab')}
              >
                <ExternalLink className='h-4 w-4' />
              </Button>
            </div>
          </DialogHeader>
          <div className='flex max-h-[75vh] items-center justify-center overflow-auto rounded-md border bg-slate-50 dark:bg-slate-900'>
            {/* eslint-disable-next-line @next/next/no-img-element -- in-memory object URL preview; next/image cannot proxy blob:// schemes */}
            <img
              src={item.objectUrl}
              alt=''
              className='max-h-[75vh] w-auto object-contain'
              draggable={false}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Reason + state */}
      <div className='flex min-w-0 flex-1 flex-col gap-1.5'>
        <div className='flex items-center gap-1.5'>
          {isMismatch ? (
            <AlertTriangle
              className='h-3.5 w-3.5 shrink-0 text-rose-500'
              aria-hidden
            />
          ) : (
            <HelpCircle
              className='h-3.5 w-3.5 shrink-0 text-amber-500'
              aria-hidden
            />
          )}
          <span
            className={cn(
              'text-xs font-semibold',
              isMismatch
                ? 'text-rose-700 dark:text-rose-300'
                : 'text-amber-700 dark:text-amber-300'
            )}
          >
            {isMismatch ? t('mismatchLabel') : t('unverifiedLabel')}
          </span>
          <StateChip resolution={item.resolution} />
        </div>
        {/* Only show the problem reason while the item is still pending —
            once resolved/accepted the StateChip carries the status. */}
        {item.resolution === 'pending' ? (
          <p className='text-muted-foreground text-[11px] leading-snug'>
            {reason}
            {item.qrData ? (
              <span className='ms-1 font-mono opacity-80'>({item.qrData})</span>
            ) : null}
          </p>
        ) : null}

        {/* Manual page-number assignment */}
        <SetPageNumberControl
          total={total}
          taken={taken}
          assigned={item.assignedPageNumber}
          onAccept={(n) => acceptItem(item.localId, n)}
        />

        {/* Actions */}
        <div className='flex flex-wrap items-center gap-1.5 pt-0.5'>
          {scannerAvailable && (
            <Button
              type='button'
              size='sm'
              variant='outline'
              className='h-7 gap-1.5 text-xs'
              onClick={() => onRescanItem(item.localId)}
            >
              <ScanLine className='h-3.5 w-3.5' />
              {t('rescanWithScanner')}
            </Button>
          )}
          <Button
            type='button'
            size='sm'
            variant='outline'
            className='h-7 gap-1.5 text-xs'
            disabled={isRendering}
            onClick={() => reuploadRef.current?.click()}
          >
            {isRendering ? (
              <Loader2 className='h-3.5 w-3.5 animate-spin' />
            ) : (
              <ImageUp className='h-3.5 w-3.5' />
            )}
            {t('reuploadImageOrPdf')}
          </Button>
          <input
            ref={reuploadRef}
            type='file'
            accept='image/*,application/pdf'
            className='hidden'
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) void handleReupload(file);
            }}
          />
          <Button
            type='button'
            size='sm'
            variant='ghost'
            className='h-7 gap-1.5 text-xs text-rose-600 hover:text-rose-700 dark:text-rose-300'
            onClick={() => removeItem(item.localId)}
          >
            <Trash2 className='h-3.5 w-3.5' />
            {t('discardPage')}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Set-page-number control (mirrors review-approve number-input pattern) ────

function SetPageNumberControl({
  total,
  taken,
  assigned,
  onAccept
}: {
  total: number;
  taken: Set<number>;
  assigned: number | undefined;
  onAccept: (pageNumber: number) => void;
}) {
  const t = useTranslations('photoScanning');
  const [value, setValue] = useState<string>(
    assigned != null ? String(assigned) : ''
  );

  const parsed = value.trim() === '' ? null : Number.parseInt(value, 10);
  const inRange =
    parsed != null && Number.isFinite(parsed) && parsed >= 1 && parsed <= total;
  const occupied = parsed != null && taken.has(parsed);
  const error = !inRange
    ? parsed != null
      ? t('pageNumberRange', { total })
      : null
    : occupied
      ? t('pageNumberOccupied', { n: parsed })
      : null;
  const canAccept = inRange && !occupied;

  return (
    <div className='flex flex-col gap-1'>
      <label className='flex items-center gap-1.5'>
        <span className='text-muted-foreground shrink-0 text-[11px] font-medium'>
          {t('pageNumberLabel')}
        </span>
        <Input
          type='number'
          min={1}
          max={total || undefined}
          step={1}
          inputMode='numeric'
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className='h-7 w-20 text-center font-mono text-xs'
          aria-invalid={error != null}
        />
        <Button
          type='button'
          size='sm'
          variant='outline'
          className='h-7 gap-1.5 text-xs'
          disabled={!canAccept}
          onClick={() => canAccept && parsed != null && onAccept(parsed)}
        >
          <Check className='h-3.5 w-3.5' />
          {t('acceptWithoutQr')}
        </Button>
      </label>
      {error ? <p className='text-[11px] text-rose-600'>{error}</p> : null}
    </div>
  );
}

// ─── Per-item state chip ──────────────────────────────────────────────────────

function StateChip({
  resolution
}: {
  resolution: QuarantineItem['resolution'];
}) {
  const t = useTranslations('photoScanning');
  if (resolution === 'pending') {
    return (
      <span className='ms-auto inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800 dark:bg-amber-950/50 dark:text-amber-200'>
        {t('statusPending')}
      </span>
    );
  }
  const isAccepted = resolution === 'accepted';
  return (
    <span
      className={cn(
        'ms-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold',
        isAccepted
          ? 'bg-sky-100 text-sky-800 dark:bg-sky-950/50 dark:text-sky-200'
          : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200'
      )}
    >
      <Check className='h-3 w-3' aria-hidden />
      {isAccepted ? t('acceptWithoutQr') : t('statusSuccess')}
    </span>
  );
}
